/**
 * Модель дашборда менеджера/владельца («Облака», волна 3 — по дизайну
 * manager v3 §3.1 «Dashboard — оперативный обзор»).
 *
 * Чистые функции расчёта KPI из реальных ответов бэка:
 *   - активные заявки / нарушен SLA — из GET /v1/admin/requests;
 *   - загрузка номеров % — из GET /v1/rooms (status === 'occupied');
 *   - заезды / выезды сегодня — из GET /v1/rooms/calendar (occupancies);
 *   - выручка/неоплачено — из GET /v1/folios (агрегат по фолио).
 *
 * Всё тестируемо без сети. i18n: лейблы — RU, без сырых англ-кодов.
 */
import type {
  CalendarResponse,
  FolioSummary,
  ManagerRequest,
  RoomView,
} from '@hsc/types';

/** Активные (незакрытые) заявки. */
export function activeRequests(items: readonly ManagerRequest[]): number {
  return items.filter((r) => !['resolved', 'cancelled'].includes(r.state)).length;
}

/** Заявки с нарушенным SLA (срок прошёл, ещё не завершена). */
export function breachedRequests(
  items: readonly ManagerRequest[],
  now: number = Date.now(),
): number {
  return items.filter((r) => {
    if (!r.sla_due_at) return false;
    if (r.resolved_at || ['resolved', 'cancelled'].includes(r.state)) return false;
    const due = new Date(r.sla_due_at).getTime();
    return !Number.isNaN(due) && due < now;
  }).length;
}

export interface Occupancy {
  /** Всего номеров. */
  total: number;
  /** Занято (status === 'occupied'). */
  occupied: number;
  /** Процент загрузки (0..100), округлён. */
  percent: number;
}

/** Загрузка номерного фонда по физическому статусу номеров. */
export function occupancy(rooms: readonly RoomView[]): Occupancy {
  const total = rooms.length;
  const occupied = rooms.filter((r) => r.status === 'occupied').length;
  const percent = total > 0 ? Math.round((occupied / total) * 100) : 0;
  return { total, occupied, percent };
}

/** YYYY-MM-DD в локали отеля (по умолчанию — текущая дата). */
export function isoDay(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface DayFlow {
  /** Заезды сегодня (occupancy.from === сегодня). */
  arrivals: number;
  /** Выезды сегодня (occupancy.to === сегодня). */
  departures: number;
}

/**
 * Заезды/выезды на дату `day` из календаря номеров. Сравниваем по дню
 * (бэк отдаёт from/to как даты ISO). Отменённые сегменты не считаем.
 */
/** Следующий день (YYYY-MM-DD) — для запроса календаря (бэк требует to > from). */
export function nextDay(day: string): string {
  const d = new Date(day + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function dayFlow(cal: CalendarResponse | undefined, day: string = isoDay()): DayFlow {
  let arrivals = 0;
  let departures = 0;
  if (!cal) return { arrivals, departures };
  for (const room of cal.rooms) {
    for (const occ of room.occupancies) {
      if (occ.status === 'cancelled' || occ.status === 'no_show') continue;
      if (occ.from.slice(0, 10) === day) arrivals += 1;
      if (occ.to.slice(0, 10) === day) departures += 1;
    }
  }
  return { arrivals, departures };
}

export interface FolioTotals {
  /** Кол-во счетов с неоплаченным остатком. */
  openFolios: number;
  /** Суммарный неоплаченный остаток. */
  unpaid: number;
  /** Валюта (берётся из первого фолио, по умолчанию RUB). */
  currency: string;
}

/** Агрегат по счетам (фолио) тенанта — для KPI «к оплате». */
export function folioTotals(items: readonly FolioSummary[]): FolioTotals {
  let unpaid = 0;
  let openFolios = 0;
  let currency = 'RUB';
  for (const f of items) {
    if (f.currency) currency = f.currency;
    const u = typeof f.unpaid === 'number' ? f.unpaid : 0;
    if (u > 0.0001) {
      unpaid += u;
      openFolios += 1;
    }
  }
  return { openFolios, unpaid, currency };
}

/** Приветствие по времени суток. */
export function greeting(d: Date = new Date()): string {
  const h = d.getHours();
  if (h < 6) return 'Доброй ночи';
  if (h < 12) return 'Доброе утро';
  if (h < 18) return 'Добрый день';
  return 'Добрый вечер';
}

/** Заголовок дашборда менеджера/владельца — без mara-специфики. */
export const DASHBOARD_HEADING = 'Оперативный обзор';
